import { __decorate } from "tslib";
import React, { Children, cloneElement, Component } from 'react';
import classNames from 'classnames';
import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import omit from 'lodash/omit';
import noop from 'lodash/noop';
import Pagination from 'choerodon-ui/pro/lib/pagination';
import ObserverCheckBox from 'choerodon-ui/pro/lib/check-box';
import autobind from 'choerodon-ui/pro/lib/_util/autobind';
import { BooleanValue, DataSetEvents } from 'choerodon-ui/pro/lib/data-set/enum';
import { getKey } from 'choerodon-ui/pro/lib/tree/util';
import Spin from '../spin';
import LocaleReceiver from '../locale-provider/LocaleReceiver';
import defaultLocale from '../locale-provider/default';
import { Size } from '../_util/enum';
import { Row } from '../grid';
import Item from './Item';
import { ListContextProvider } from './ListContext';
import ConfigContext from '../config-provider/ConfigContext';
export { ListItemProps, ListItemMetaProps } from './Item';
let List = class List extends Component {
    constructor() {
        super(...arguments);
        this.defaultPaginationProps = {
            page: 1,
            pageSize: 10,
            onChange: (page, pageSize) => {
                const { pagination } = this.props;
                this.paginationCurrent = page;
                if (pagination && pagination.onChange) {
                    pagination.onChange(page, pageSize);
                }
            },
            total: 0,
        };
        this.keys = {};
        this.renderItem = (item, index) => {
            const { renderItem, rowKey, dataSet } = this.props;
            let key;
            if (typeof rowKey === 'function') {
                key = rowKey(item);
            }
            else if (typeof rowKey === 'string') {
                key = dataSet ? item.get(rowKey) : item[rowKey];
            }
            else {
                key = item.key;
            }
            if (!key) {
                key = `list-item-${index}`;
            }
            this.keys[index] = key;
            if (dataSet) {
                return renderItem({ dataSet, record: item, index: dataSet.indexOf(item) });
            }
            return renderItem(item, index);
        };
        this.renderEmpty = (contextLocale) => {
            const { props } = this;
            const locale = { ...contextLocale, ...props.locale };
            return React.createElement("div", { className: `${this.getPrefixCls()}-empty-text` }, locale.emptyText);
        };
    }
    static get contextType() {
        return ConfigContext;
    }
    get rowSelectionKeys() {
        const { rowSelection } = this.props;
        if (rowSelection && typeof rowSelection === 'object') {
            const { selectedRowKeys, defaultSelectedRowKeys } = rowSelection;
            return selectedRowKeys || defaultSelectedRowKeys;
        }
    }
    get checkedKeys() {
        const { dataSet } = this.props;
        if (dataSet) {
            const { checkField, primaryKey } = dataSet.props;
            if (checkField) {
                const keys = [];
                const field = dataSet.getField(checkField);
                dataSet.forEach(record => {
                    const key = getKey(record, primaryKey);
                    if (record.get(checkField) === (field ? field.get(BooleanValue.trueValue, record) : true)) {
                        keys.push(key);
                    }
                });
                return keys;
            }
            return dataSet.selected.map(selected => String(primaryKey ? selected.get(primaryKey) : selected.id));
        }
        return this.rowSelectionKeys || this.stateCheckedKeys || [];
    }
    get paginationProps() {
        const { dataSet, dataSource, pagination } = this.props;
        const { pageSize, ...otherProps } = this.defaultPaginationProps;
        const paginationProps = {
            ...otherProps,
            ...pagination || {},
            total: dataSet ? dataSet.totalCount : dataSource.length,
            page: (dataSet && dataSet.paging) ? dataSet.currentPage : (this.paginationCurrent || 1),
            pageSize: (dataSet && dataSet.paging) ? dataSet.pageSize : (pagination && pagination.pageSize || pageSize),
        };
        return paginationProps;
    }
    componentWillMount() {
        this.handleDataSetLoad();
        this.processDataSetListener(true);
        runInAction(() => {
            this.stateCheckedKeys = [];
        });
    }
    componentWillUnmount() {
        this.processDataSetListener(false);
    }
    handleDataSetLoad() {
        this.initDefaultCheckRows();
    }
    processDataSetListener(flag) {
        const { dataSet } = this.props;
        if (dataSet) {
            const handler = flag ? dataSet.addEventListener : dataSet.removeEventListener;
            handler.call(dataSet, DataSetEvents.load, this.handleDataSetLoad);
        }
    }
    initDefaultCheckRows() {
        const { props: { dataSet, }, } = this;
        if (dataSet && dataSet.selection) {
            const { checkField } = dataSet.props;
            if (checkField) {
                const field = dataSet.getField(checkField);
                dataSet.forEach(record => {
                    if (record.get(checkField) === (field ? field.get(BooleanValue.trueValue, record) : true)) {
                        record.isSelected = true;
                    }
                });
            }
        }
    }
    getContextValue() {
        const { grid } = this.props;
        const { getPrefixCls } = this.context;
        return {
            grid,
            getPrefixCls,
        };
    }
    isSomethingAfterLastItem() {
        const { loadMore, pagination, footer } = this.props;
        return !!(loadMore || pagination || footer);
    }
    getPrefixCls() {
        const { prefixCls } = this.props;
        const { getPrefixCls } = this.context;
        return getPrefixCls('list', prefixCls);
    }
    updateStateKey(value, key) {
        if (value && !this.stateCheckedKeys.includes(key)) {
            this.stateCheckedKeys = [...this.stateCheckedKeys, key];
        }
        else if (!value) {
            this.stateCheckedKeys = this.stateCheckedKeys.filter(x => x !== key);
        }
        return this.stateCheckedKeys;
    }
    handleChange(value, key) {
        const { rowSelection, dataSet } = this.props;
        if (dataSet && dataSet.selection) {
            const { primaryKey } = dataSet.props;
            dataSet.forEach(record => {
                if (getKey(record, primaryKey) === key) {
                    if (!record.isSelected) {
                        dataSet.select(record);
                    }
                    else {
                        dataSet.unSelect(record);
                    }
                }
            });
            return;
        }
        if (rowSelection) {
            if (typeof rowSelection === 'object') {
                const { selectedRowKeys, onChange = noop } = rowSelection;
                let resultKeys = [];
                if (selectedRowKeys) {
                    if (value && !selectedRowKeys.includes(key)) {
                        resultKeys = [...selectedRowKeys, key];
                    }
                    else if (!value) {
                        resultKeys = selectedRowKeys.filter(x => x !== key);
                    }
                    onChange(resultKeys);
                    return;
                }
                const newKeys = this.updateStateKey(value, key);
                onChange(newKeys);
            }
            else if (typeof rowSelection === 'boolean') {
                this.updateStateKey(value, key);
            }
        }
    }
    renderCheckBox(key) {
        const { rowSelection, dataSet } = this.props;
        const checkboxWrapper = () => {
            const isChecked = this.checkedKeys.includes(key);
            return {
                element: () => (React.createElement("div", { className: `${this.getPrefixCls()}-selection-checkbox` },
                    React.createElement(ObserverCheckBox, { name: "ckBox", value: key, onChange: (val) => this.handleChange(val, key), checked: isChecked }))),
                isChecked,
            };
        };
        if (dataSet) {
            if (dataSet.selection) {
                return checkboxWrapper();
            }
            return undefined;
        }
        if (rowSelection) {
            return checkboxWrapper();
        }
        return undefined;
    }
    render() {
        const { paginationProps: { pageSize, page, total, position, onChange = noop }, props: { bordered, split, className, children, itemLayout, loadMore, pagination, grid, dataSource, size, header, footer, empty, loading, rowPrefixCls, spinPrefixCls, dataSet, ...rest }, } = this;
        const prefixCls = this.getPrefixCls();
        let loadingProp = loading;
        if (typeof loadingProp === 'boolean') {
            loadingProp = {
                spinning: loadingProp,
            };
        }
        const isLoading = loadingProp && loadingProp.spinning;
        // large => lg
        // small => sm
        let sizeCls = '';
        switch (size) {
            case Size.large:
                sizeCls = 'lg';
                break;
            case Size.small:
                sizeCls = 'sm';
                break;
            default:
        }
        const classString = classNames(prefixCls, className, {
            [`${prefixCls}-vertical`]: itemLayout === 'vertical',
            [`${prefixCls}-${sizeCls}`]: sizeCls,
            [`${prefixCls}-split`]: split,
            [`${prefixCls}-bordered`]: bordered,
            [`${prefixCls}-loading`]: isLoading,
            [`${prefixCls}-grid`]: grid,
            [`${prefixCls}-something-after-last-item`]: this.isSomethingAfterLastItem(),
        });
        const largestPage = Math.ceil(total / pageSize);
        if (page > largestPage) {
            this.paginationProps.page = largestPage;
        }
        const isDsPagination = (dataSet && dataSet.paging);
        const paginationContent = pagination ? (React.createElement("div", { className: `${prefixCls}-pagination` },
            React.createElement(Pagination, Object.assign({}, this.paginationProps, { onChange: onChange, dataSet: isDsPagination ? dataSet : undefined })))) : null;
        let splitDataSource = dataSet ? dataSet.records : [...dataSource];
        if (pagination && pageSize) {
            if (!dataSet &&
                dataSource.length > (page - 1) * pageSize) {
                splitDataSource = [...dataSource].splice((page - 1) * pageSize, pageSize);
            }
            else if (dataSet && !dataSet.paging) {
                splitDataSource = dataSet.slice((page - 1) * pageSize, pageSize * page);
            }
        }
        let childrenContent;
        childrenContent = isLoading && React.createElement("div", { style: { minHeight: 53 } });
        if (splitDataSource.length > 0) {
            const items = splitDataSource.map((item, index) => this.renderItem(item, index));
            const childrenList = Children.map(items, (child, index) => {
                const recordKey = this.keys[index];
                return cloneElement(child, {
                    key: recordKey,
                    renderCheckBox: this.renderCheckBox(recordKey),
                });
            });
            childrenContent = grid ? React.createElement(Row, { prefixCls: rowPrefixCls, gutter: grid.gutter }, childrenList) : childrenList;
        }
        else if (!children && !isLoading && !empty) {
            childrenContent = (React.createElement(LocaleReceiver, { componentName: "Table", defaultLocale: defaultLocale.Table }, this.renderEmpty));
        }
        else {
            childrenContent = empty;
        }
        const paginationPosition = position || 'bottom';
        const content = (React.createElement(Spin, Object.assign({ prefixCls: spinPrefixCls }, loadingProp),
            childrenContent,
            children));
        return (React.createElement(ListContextProvider, Object.assign({}, this.getContextValue()),
            React.createElement("div", Object.assign({ className: classString }, omit(rest, ['prefixCls', 'rowKey', 'renderItem', 'selectable', 'renderItem', 'locale', 'rowSelection'])),
                (paginationPosition === 'top' || paginationPosition === 'both') && paginationContent,
                header && React.createElement("div", { className: `${prefixCls}-header` }, header),
                content,
                footer && React.createElement("div", { className: `${prefixCls}-footer` }, footer),
                loadMore || (paginationPosition === 'bottom' || paginationPosition === 'both') && paginationContent)));
    }
};
List.displayName = 'List';
List.Item = Item;
List.defaultProps = {
    dataSource: [],
    bordered: false,
    split: true,
    loading: false,
    pagination: false,
};
__decorate([
    observable
], List.prototype, "stateCheckedKeys", void 0);
__decorate([
    observable
], List.prototype, "paginationCurrent", void 0);
__decorate([
    autobind
], List.prototype, "handleDataSetLoad", null);
__decorate([
    action
], List.prototype, "initDefaultCheckRows", null);
__decorate([
    action
], List.prototype, "updateStateKey", null);
List = __decorate([
    observer
], List);
export default List;
